import { StandardEvents } from '@shopify/events'
import { scheduleAlmaWidgetRefresh } from '@theme/alma-widget-sync'
import { updateHtPriceElement } from '@theme/price-excluding-tax'

/**
 * PGB bundle UI sync for the product information section: reconciles bundle pickers with
 * summary, header price, and sticky ATC when selection or variant changes.
 *
 * **Money format contract (`PDP-005`):**
 * - The `<bundle-product-sync>` element receives `data-money-format` from Liquid
 *   (`{{ shop.money_format | escape }}` in [`sections/product-information.liquid`](../sections/product-information.liquid)).
 *   The host reads it as `this.dataset.moneyFormat`, falling back to {@link DEFAULT_MONEY_FORMAT}.
 * - {@link formatMoney} interprets Shopify money templates (e.g. `{{amount}}`, `amount_with_comma_separator`) so
 *   client-side totals stay consistent with Liquid `| money` for the supported placeholder set (see also
 *   [`assets/money-formatting.js`](../assets/money-formatting.js)).
 * - Merchant-defined formats that embed HTML still pass through `| escape` safely in the attribute string;
 *   if a format is exotic enough that regex-based substitution diverges from Liquid output, treat that as
 *   a merchant/settings edge case.
 *
 * **PDP-001:** Full Section Rendering + `morph()` for bundle-driven surfaces is documented as deferred in
 * [`docs/bundle-product-sync.md`](../docs/bundle-product-sync.md) (server has no PGB selection context).
 */

/** @constant Fallback when `data-money-format` is missing; matches a bare Shopify amount token. */
const DEFAULT_MONEY_FORMAT = '{' + '{amount}}'
const SUMMARY_PRICE_SELECTORS = [
  '[data-product-info-summary-price]',
  '.product-info-summary__price-row'
]

/**
 * PDP-006 — Single configuration table describing every bundle-summary recap surface
 * the section ships. The marker `data-bundle-summary-source="<key>"` in Liquid binds a
 * DOM container to one of these configs; `applyBundleItemsToSurface` then drives all
 * surfaces through one update path, replacing the previous parallel summary / sticky
 * branches in `#cleanupBundleVariants`.
 *
 * @typedef {Object} BundleSummarySurfaceConfig
 * @property {string} itemContainerSelector Selector relative to the marker element pointing at the list that holds variant items (`null` ⇒ marker itself).
 * @property {string} itemBaseClass Base class shared by all variant items in this surface (used to count items / detect emptiness).
 * @property {string} itemBundleClass Class added on bundle-injected items so they can be cleanly removed before re-render.
 * @property {string} itemOptionClass Class added on product-option-injected items so they can be cleanly removed before re-render.
 * @property {string} valueClass Class on the inner element that displays each variant value.
 * @property {'span' | 'li'} itemTag Tag used when creating bundle items so output matches the SSR'd shape.
 * @property {string | null} hideTargetSelector Selector (relative to marker) for the element whose `hidden` attribute mirrors emptiness; `null` ⇒ marker itself.
 * @property {string | null} accessibilityTextSelector Selector (relative to marker) for the screen-reader recap text node, or `null` when not used.
 *
 * @type {Record<string, BundleSummarySurfaceConfig>}
 */
export const BUNDLE_SUMMARY_SURFACE_CONFIGS = {
  summary: {
    itemContainerSelector: '',
    itemBaseClass: 'product-info-summary__variant-item',
    itemBundleClass: 'product-info-summary__variant-item--bundle',
    itemOptionClass: 'product-info-summary__variant-item--option',
    valueClass: 'product-info-summary__variant-value',
    itemTag: 'span',
    hideTargetSelector: '',
    accessibilityTextSelector: null
  },
  sticky: {
    itemContainerSelector: '.sticky-atc__variants',
    itemBaseClass: 'sticky-atc__variant-item',
    itemBundleClass: 'sticky-atc__variant-item--bundle',
    itemOptionClass: 'sticky-atc__variant-item--option',
    valueClass: 'sticky-atc__variant-value',
    itemTag: 'li',
    hideTargetSelector: '',
    accessibilityTextSelector: '.sticky-atc__variants-text'
  }
}

/**
 * @typedef {Object} BundleItem
 * @property {string} productTitle
 * @property {number} originalPriceCents
 * @property {number} finalPriceCents
 * @property {string} [variantId] Numeric variant id as a string; used when building the cart payload.
 * @property {string} [productId] Product GID (e.g. `gid://shopify/Product/123`).
 * @property {string} [groupId] PGB group UUID, used to write `_bundle_group_id` per child.
 * @property {string} [parentProductGid] Parent product GID for the bundle child, used to write `_bundle_parent_product_id`.
 * @property {boolean} [isRequiredGroup] Whether the child belongs to a required bundle group.
 */

/**
 * @typedef {Object} BundleState
 * @property {BundleItem[]} selectedItems
 * @property {number} totalOriginalPriceCents
 * @property {number} totalFinalPriceCents
 */

/**
 * @returns {BundleState}
 */
export function createEmptyBundleState() {
  return {
    selectedItems: [],
    totalOriginalPriceCents: 0,
    totalFinalPriceCents: 0
  }
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function majorUnitsToCents(value) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return 0

  return Math.round(numericValue * 100)
}

/**
 * @param {string | undefined} value
 * @returns {number}
 */
export function parseInteger(value) {
  const numericValue = Number.parseInt(value || '', 10)
  if (!Number.isFinite(numericValue)) return 0

  return numericValue
}

/**
 * @param {number} basePriceCents
 * @param {number} baseComparePriceCents
 * @param {BundleState} bundleState
 * @param {number} [optionAdjustmentCents] Product-option surcharge, already in cents.
 * @returns {{ totalPriceCents: number, totalComparePriceCents: number, totalDiscountCents: number, hasDiscount: boolean }}
 */
export function computeBundlePriceTotals(
  basePriceCents,
  baseComparePriceCents,
  bundleState,
  optionAdjustmentCents = 0
) {
  const optionCents = Number.isFinite(optionAdjustmentCents)
    ? optionAdjustmentCents
    : 0
  const totalPriceCents =
    basePriceCents + optionCents + bundleState.totalFinalPriceCents
  const totalComparePriceCents =
    baseComparePriceCents + optionCents + bundleState.totalOriginalPriceCents
  const totalDiscountCents = Math.max(
    0,
    totalComparePriceCents - totalPriceCents
  )
  const hasDiscount = totalComparePriceCents > totalPriceCents

  return {
    totalPriceCents,
    totalComparePriceCents,
    totalDiscountCents,
    hasDiscount
  }
}

/**
 * @param {string | undefined} template from `data-bundle-save-template` (uses `{amount}` placeholder)
 * @param {string} formattedMoney
 * @returns {string} Interpolated label, or formatted money only if the template is missing `{amount}`
 */
export function formatBundleSaveDisplay(template, formattedMoney) {
  const money = String(formattedMoney ?? '').trim()
  const tpl = String(template ?? '').trim()
  if (!money) return ''
  if (tpl.includes('{amount}')) return tpl.replaceAll('{amount}', money)
  return money
}

/**
 * @param {BundleItem[]} selectedItems
 * @returns {BundleState}
 */
export function buildBundleState(selectedItems) {
  const sanitizedItems = selectedItems.filter((item) => item.productTitle)
  const totalOriginalPriceCents = sanitizedItems.reduce((total, item) => {
    return total + item.originalPriceCents
  }, 0)
  const totalFinalPriceCents = sanitizedItems.reduce((total, item) => {
    return total + item.finalPriceCents
  }, 0)

  return {
    selectedItems: sanitizedItems,
    totalOriginalPriceCents,
    totalFinalPriceCents
  }
}

/**
 * @param {unknown} detail
 * @returns {BundleState}
 */
export function buildBundleStateFromEvent(detail) {
  if (
    !detail ||
    typeof detail !== 'object' ||
    !Array.isArray(detail.selectedItems)
  ) {
    return createEmptyBundleState()
  }

  const selectedItems = detail.selectedItems.map((item) => {
    const originalPriceCents = majorUnitsToCents(item?.originalPrice)
    const finalPriceCents = majorUnitsToCents(item?.finalPrice)

    return {
      productTitle: String(item?.productTitle || '').trim(),
      originalPriceCents,
      finalPriceCents,
      variantId: item?.variantId ? String(item.variantId) : undefined,
      productId: item?.productId ? String(item.productId) : undefined,
      groupId: item?.groupId ? String(item.groupId) : undefined,
      parentProductGid: item?.parentProductGid
        ? String(item.parentProductGid)
        : undefined,
      isRequiredGroup: item?.isRequiredGroup === true || item?.required === true
    }
  })

  return buildBundleState(selectedItems)
}

/**
 * @param {Element | null} picker
 * @returns {BundleState}
 */
export function buildBundleStateFromPicker(picker) {
  if (!(picker instanceof Element)) return createEmptyBundleState()

  const selectedItems = Array.from(
    picker.querySelectorAll('.pgb-checkbox:checked')
  ).map((checkbox) => {
    const originalPriceCents = majorUnitsToCents(
      checkbox.getAttribute('data-price')
    )
    const discountCents = majorUnitsToCents(
      checkbox.getAttribute('data-discount')
    )
    const finalPriceCents = Math.max(0, originalPriceCents - discountCents)
    const variantId =
      checkbox.getAttribute('data-variant-id')?.trim() || undefined
    const productId =
      checkbox.getAttribute('data-product-id')?.trim() || undefined
    const groupId = checkbox.getAttribute('data-group-id')?.trim() || undefined
    const parentProductGid =
      checkbox.getAttribute('data-parent-product-gid')?.trim() || undefined
    const group = checkbox.closest('.pgb-group')
    const isRequiredGroup =
      group?.getAttribute('data-selection-required') === 'true'

    return {
      productTitle: checkbox.getAttribute('data-product-title')?.trim() || '',
      originalPriceCents,
      finalPriceCents,
      variantId,
      productId,
      groupId,
      parentProductGid,
      isRequiredGroup
    }
  })

  return buildBundleState(selectedItems)
}

/**
 * @typedef {Object} OptionItem
 * @property {string} label Merchant-facing option label (already localized by the app).
 * @property {number} adjustmentCents Price surcharge in cents (never major units).
 * @property {string} [optionId]
 * @property {string} [groupId]
 */

/**
 * @typedef {Object} OptionsState
 * @property {OptionItem[]} selectedItems
 * @property {number} adjustmentCents
 */

/**
 * @returns {OptionsState}
 */
export function createEmptyOptionsState() {
  return {
    selectedItems: [],
    adjustmentCents: 0
  }
}

/**
 * @param {OptionItem[]} selectedItems
 * @returns {OptionsState}
 */
function buildOptionsState(selectedItems) {
  const sanitizedItems = selectedItems.filter((item) => item.label)
  const adjustmentCents = sanitizedItems.reduce((total, item) => {
    return total + item.adjustmentCents
  }, 0)

  return { selectedItems: sanitizedItems, adjustmentCents }
}

/**
 * Read the current product-option selection from a `<pgb-product-options>` element.
 * `data-adjustment` and `getSelectedOptions()` both expose CENTS, so no conversion runs here.
 *
 * @param {Element | null | undefined} element
 * @returns {OptionsState}
 */
export function buildOptionsStateFromElement(element) {
  if (!(element instanceof Element)) return createEmptyOptionsState()

  const inputs = element.querySelectorAll('.pgo-choice-input')

  if (inputs.length > 0) {
    const selectedItems = Array.from(inputs)
      .filter((input) => input instanceof HTMLInputElement && input.checked)
      .map((input) => {
        const choice = /** @type {HTMLInputElement} */ (input)

        return {
          label: (choice.dataset.optionLabel || '').trim(),
          adjustmentCents: parseInteger(choice.dataset.adjustment),
          optionId: choice.dataset.optionId?.trim() || undefined,
          groupId: choice.dataset.groupId?.trim() || undefined
        }
      })

    return buildOptionsState(selectedItems)
  }

  const optionsApi =
    /** @type {Element & { getSelectedOptions?: () => unknown[] }} */ (element)

  if (typeof optionsApi.getSelectedOptions !== 'function') {
    return createEmptyOptionsState()
  }

  const rawItems = optionsApi.getSelectedOptions()
  if (!Array.isArray(rawItems)) return createEmptyOptionsState()

  const selectedItems = rawItems.map((item) => {
    const source = /** @type {Record<string, unknown>} */ (item || {})
    const adjustment = Number(source.adjustment)

    return {
      label: String(source.optionLabel ?? source.label ?? '').trim(),
      adjustmentCents: Number.isFinite(adjustment) ? Math.round(adjustment) : 0,
      optionId: source.optionId ? String(source.optionId) : undefined,
      groupId: source.groupId ? String(source.groupId) : undefined
    }
  })

  return buildOptionsState(selectedItems)
}

/**
 * Reads `<pgb-product-options>.isSelectionValid()` defensively.
 *
 * Unlike its `getSelectedOptions()` / `getCartProperties()` siblings, that method
 * is not safe to call unconditionally: it resolves through the app's `payload()`,
 * which dereferences a nullable parsed config. The app renders the block whenever
 * the stored metafield says `enabled`, so a config the app's own client-side
 * schema validation rejects (version drift) leaves a live element whose validity
 * check throws.
 *
 * A throw is resolved against what the element actually rendered. The app only
 * emits `aria-required` groups for selections it means to enforce, so their
 * presence means the shopper has something to satisfy and an uncomputable
 * validity must not pass. With no required group rendered there is nothing to
 * fix, and refusing the add would strand the shopper on a broken config.
 *
 * An element that is still the raw `<pgb-product-options>` tag, with no
 * `isSelectionValid`, has not loaded `product-options.js`. That is not valid:
 * the choices are not on screen, and a bare add is rejected by cart validation.
 *
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
export function readOptionsSelectionValid(element) {
  const optionsApi =
    /** @type {Element & { isSelectionValid?: () => boolean }} */ (element)

  if (!optionsApi) return true

  // The tag was cloned before product-options.js defined it. Treating that as
  // valid posts a bare variant and the cart rejects the required warranty.
  if (typeof optionsApi.isSelectionValid !== 'function') {
    return optionsApi.tagName !== 'PGB-PRODUCT-OPTIONS'
  }

  try {
    return optionsApi.isSelectionValid()
  } catch (error) {
    const hasRequiredGroup = Boolean(
      optionsApi.querySelector?.('.pgo-group[aria-required="true"]')
    )
    console.error(
      '[bundle-product-sync] pgb-product-options isSelectionValid() threw',
      error
    )
    return !hasRequiredGroup
  }
}

/**
 * Resolve whether required product-option selections are satisfied.
 * Defaults to true when validation metadata is absent (backward compatible).
 *
 * @param {unknown} detail
 * @param {Element | null} [element]
 * @returns {boolean}
 */
export function resolveOptionsSelectionValid(detail, element = null) {
  if (detail && typeof detail === 'object') {
    const source = /** @type {Record<string, any>} */ (detail)

    if (
      source.validation &&
      typeof source.validation === 'object' &&
      typeof source.validation.isValid === 'boolean'
    ) {
      return source.validation.isValid
    }

    if (typeof source.isValid === 'boolean') return source.isValid
  }

  return readOptionsSelectionValid(element)
}

/**
 * @param {OptionsState} optionsState
 * @returns {string}
 */
export function computeOptionsStateKey(optionsState) {
  if (!optionsState || optionsState.selectedItems.length === 0) return 'empty'

  return optionsState.selectedItems
    .map((item) => `${item.optionId ?? item.label}:${item.adjustmentCents}`)
    .join('|')
}

const VARIANT_GID_PREFIX = 'gid://shopify/ProductVariant/'

/**
 * `<pgb-product-options>` stores its variant scope as a ProductVariant GID and
 * normalizes anything it receives the same way. Mirroring that normalization
 * here lets us compare the app's scope against the numeric id the theme is
 * about to POST without false mismatches.
 *
 * @param {unknown} value
 * @returns {string} Normalized GID, or `''` when `value` holds no variant id.
 */
export function normalizeVariantGid(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  if (raw.startsWith(VARIANT_GID_PREFIX)) return raw

  const tail = raw.split('/').pop() ?? ''
  return /^\d+$/.test(tail) ? `${VARIANT_GID_PREFIX}${tail}` : ''
}

/**
 * Dispatches the `variant:change` event `<pgb-product-options>` listens for on
 * `document`. Synchronous by design: callers that need the app's scope updated
 * before reading `getCartProperties()` rely on the listener having run.
 *
 * @param {unknown} variantId
 * @param {unknown} [priceCents] Variant price in cents (the app's `data-base-price` contract).
 * @returns {boolean} `true` when an event was dispatched.
 */
export function dispatchVariantChangeBridge(variantId, priceCents) {
  const normalized = normalizeVariantGid(variantId)
  if (!normalized) return false

  /** @type {{ id: string, price?: number }} */
  const variant = { id: normalized }
  const price = Number(priceCents)
  if (Number.isFinite(price)) variant.price = price

  document.dispatchEvent(
    new CustomEvent('variant:change', { detail: { variant } })
  )

  return true
}

/**
 * Aligns the app's variant scope with the variant the theme is about to submit.
 *
 * The app builds `_pgo_selections` / `_pgo_snapshot` against its own
 * `variantId`, while the theme POSTs the buy-button form's `[name="id"]`. When
 * the two disagree — a missed bridge, a morph that replaced the block, or a
 * `ProductSelectEvent` that resolved without a variant resource — the cart line
 * carries selections for groups that do not apply to the submitted variant, and
 * the app's cart validation function rejects the whole cart with its generic
 * "options are no longer valid" error. Re-dispatching the bridge makes the app
 * re-derive applicable groups (and its defaults) before we read properties.
 *
 * @param {Element | null | undefined} optionsEl
 * @param {unknown} variantId Variant id being submitted.
 * @returns {boolean} `true` when the app's scope matches the submitted variant.
 */
export function syncProductOptionsVariant(optionsEl, variantId) {
  if (!(optionsEl instanceof Element)) return true

  const expected = normalizeVariantGid(variantId)
  if (!expected) return true

  const readScope = () =>
    normalizeVariantGid(
      /** @type {Element & { variantId?: unknown }} */ (optionsEl).variantId
    )

  const current = readScope()
  // An empty scope means the block never initialized (no config, or the data
  // island is missing); it will not contribute cart properties either.
  if (!current || current === expected) return true

  dispatchVariantChangeBridge(expected)

  return readScope() === expected
}

const BUNDLE_BUY_BUTTON_SELECTOR =
  'product-form-component [type="submit"], .sticky-atc__button'

/**
 * Resolve whether required bundle selections are satisfied.
 * Defaults to true when validation metadata is absent (backward compatible).
 *
 * @param {unknown} detail
 * @param {Element | null} [picker]
 * @returns {boolean}
 */
export function resolveBundleSelectionValid(detail, picker = null) {
  if (
    detail &&
    typeof detail === 'object' &&
    'validation' in detail &&
    detail.validation &&
    typeof detail.validation === 'object' &&
    typeof detail.validation.isValid === 'boolean'
  ) {
    return detail.validation.isValid
  }

  const pickerWithValidation =
    /** @type {Element & { getSelectionValidation?: () => { isValid?: boolean }, isSelectionValid?: () => boolean }} */ (
      picker
    )

  if (
    pickerWithValidation &&
    typeof pickerWithValidation.getSelectionValidation === 'function'
  ) {
    const validation = pickerWithValidation.getSelectionValidation()
    if (typeof validation?.isValid === 'boolean') {
      return validation.isValid
    }
  }

  if (
    pickerWithValidation &&
    typeof pickerWithValidation.isSelectionValid === 'function'
  ) {
    return pickerWithValidation.isSelectionValid()
  }

  return true
}

/**
 * Toggle buy buttons from required-bundle / required-option validation without
 * clobbering sold-out state.
 *
 * @param {ParentNode | null | undefined} section
 * @param {boolean} isValid
 * @param {Element | null} [picker]
 * @param {Element | null} [options] `<pgb-product-options>`, when present.
 */
export function syncBundleBuyButtons(
  section,
  isValid,
  picker = null,
  options = null
) {
  if (!(section instanceof Element)) return

  const resolvedPicker =
    picker instanceof Element
      ? picker
      : section.querySelector('pgb-bundle-picker')
  const resolvedOptions =
    options instanceof Element
      ? options
      : section.querySelector('pgb-product-options')

  const canBlockCheckout =
    pickerHasRequiredGroups(resolvedPicker) ||
    resolvedOptions instanceof Element

  const acceleratedCheckout = section.querySelector(
    '[ref="acceleratedCheckoutButtonContainer"]'
  )
  if (acceleratedCheckout instanceof HTMLElement) {
    if (canBlockCheckout && !isValid) {
      // Remember that *we* hid it so a sold-out variant hidden by
      // `product-form.js` is never un-hidden on the next valid pass.
      acceleratedCheckout.dataset.pgbSelectionHidden = 'true'
      acceleratedCheckout.setAttribute('hidden', 'true')
    } else if (acceleratedCheckout.dataset.pgbSelectionHidden === 'true') {
      delete acceleratedCheckout.dataset.pgbSelectionHidden
      acceleratedCheckout.removeAttribute('hidden')
    }
  }

  const buttons = section.querySelectorAll(BUNDLE_BUY_BUTTON_SELECTOR)

  buttons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return

    if (!isValid) {
      if (button.dataset.pgbSelectionBlocked !== 'true') {
        button.dataset.pgbSelectionWasDisabled = button.disabled
          ? 'true'
          : 'false'
      }
      button.dataset.pgbSelectionBlocked = 'true'
      button.disabled = true
      button.setAttribute('aria-disabled', 'true')
      return
    }

    if (button.dataset.pgbSelectionBlocked !== 'true') return

    const wasDisabled = button.dataset.pgbSelectionWasDisabled === 'true'
    delete button.dataset.pgbSelectionBlocked
    delete button.dataset.pgbSelectionWasDisabled
    button.removeAttribute('aria-disabled')
    button.disabled = wasDisabled
  })
}

/** Maximum rAF bootstrap passes while waiting for PGB auto-select on load. */
export const PICKER_BOOTSTRAP_MAX_ATTEMPTS = 12

/**
 * @typedef {Object} PickerSelectionSnapshot
 * @property {number} checkedCount
 * @property {boolean} isValid
 * @property {string} key Stable comparison key for consecutive-frame checks.
 */

/**
 * @param {Element | null | undefined} picker
 * @returns {boolean}
 */
export function pickerHasRequiredGroups(picker) {
  if (!(picker instanceof Element)) return false

  return picker.querySelector('[data-selection-required="true"]') !== null
}

/**
 * @param {Element | null | undefined} picker
 * @returns {PickerSelectionSnapshot}
 */
export function readPickerSelectionSnapshot(picker) {
  if (!(picker instanceof Element)) {
    return { checkedCount: 0, isValid: true, key: '0:true' }
  }

  const checkedCount = picker.querySelectorAll('.pgb-checkbox:checked').length
  const isValid = resolveBundleSelectionValid(null, picker)

  return {
    checkedCount,
    isValid,
    key: `${checkedCount}:${isValid}`
  }
}

/**
 * @param {Element | null | undefined} picker
 * @param {PickerSelectionSnapshot} snapshot
 * @param {number} attempt
 * @returns {boolean}
 */
export function shouldContinuePickerBootstrap(picker, snapshot, attempt) {
  if (!(picker instanceof Element)) return false
  if (attempt >= PICKER_BOOTSTRAP_MAX_ATTEMPTS) return false
  if (!pickerHasRequiredGroups(picker)) return false
  if (snapshot.isValid) return false

  return true
}

/**
 * @returns {string}
 */
export function createBundleInstanceId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return `pgb_${globalThis.crypto.randomUUID()}`
  }

  const timestamp = Date.now().toString(36)
  const randomPart = Math.random().toString(36).slice(2, 12)

  return `pgb_${timestamp}_${randomPart}`
}

/**
 * @typedef {Object} BundlePayloadItem
 * @property {number} id Numeric variant id.
 * @property {number} quantity Quantity for this line.
 * @property {number} [parent_id] Native Shopify parent variant id used for bundle grouping.
 * @property {Record<string, string>} [properties] Line item properties (PGB-compatible).
 */

/**
 * Build the `items[]` payload for the Cart Ajax API. The first item is the
 * main variant; any selected bundle children follow with `parent_id` pointing
 * at the main variant and PGB-compatible line item properties so the storefront
 * cart still groups bundles even though the theme owns submission.
 *
 * @param {string | number} mainVariantId Main variant id (numeric).
 * @param {BundleState} bundleState Bundle state from the picker.
 * @param {object} [options]
 * @param {number} [options.quantity=1] Quantity for the main variant.
 * @param {Record<string, string>} [options.mainProperties] Line item properties for the main variant.
 * @returns {BundlePayloadItem[]}
 */
export function buildBundlePayloadItems(
  mainVariantId,
  bundleState,
  options = {}
) {
  const numericMainId = Number(mainVariantId)
  const quantity = Number.isFinite(options.quantity)
    ? Math.max(1, Math.trunc(/** @type {number} */ (options.quantity)))
    : 1

  /** @type {BundlePayloadItem} */
  const mainItem = {
    id: numericMainId,
    quantity
  }

  if (
    options.mainProperties &&
    typeof options.mainProperties === 'object' &&
    Object.keys(options.mainProperties).length > 0
  ) {
    mainItem.properties = { ...options.mainProperties }
  }

  /** @type {BundlePayloadItem[]} */
  const childItems = []

  if (!bundleState || !Array.isArray(bundleState.selectedItems)) {
    return [mainItem]
  }

  for (const item of bundleState.selectedItems) {
    const childVariantId = Number(item?.variantId)
    if (!Number.isFinite(childVariantId) || childVariantId <= 0) continue

    /** @type {Record<string, string>} */
    const properties = {}
    if (item.parentProductGid) {
      properties._bundle_parent_product_id = item.parentProductGid
    }
    if (item.groupId) {
      properties._bundle_group_id = item.groupId
    }
    if (item.isRequiredGroup) {
      properties._bundle_group_required = 'true'
    }

    /** @type {BundlePayloadItem} */
    const childItem = {
      id: childVariantId,
      quantity: 1
    }

    if (Number.isFinite(numericMainId) && numericMainId > 0) {
      childItem.parent_id = numericMainId
    }

    if (Object.keys(properties).length > 0) {
      childItem.properties = properties
    }

    childItems.push(childItem)
  }

  if (childItems.length === 0) {
    return [mainItem]
  }

  const bundleInstanceId = createBundleInstanceId()
  mainItem.properties = {
    ...(mainItem.properties || {}),
    _bundle_instance_id: bundleInstanceId
  }

  for (const childItem of childItems) {
    childItem.properties = {
      ...(childItem.properties || {}),
      _bundle_instance_id: bundleInstanceId
    }
  }

  return [mainItem, ...childItems]
}

/**
 * @param {number} cents
 * @param {string} thousandsSeparator
 * @param {string} decimalSeparator
 * @param {number} precision
 * @param {boolean} stripTrailingZeros
 * @returns {string}
 */
function formatCents(
  cents,
  thousandsSeparator,
  decimalSeparator,
  precision,
  stripTrailingZeros
) {
  const shouldStripDecimals =
    stripTrailingZeros && precision > 0 && cents % 100 === 0
  const normalizedPrecision = shouldStripDecimals ? 0 : precision
  const roundedNumber = (cents / 100).toFixed(normalizedPrecision)

  let [wholePart, decimalPart] = roundedNumber.split('.')

  if (!wholePart) wholePart = '0'
  if (!decimalPart) decimalPart = ''

  wholePart = wholePart.replace(
    /\d(?=(\d\d\d)+(?!\d))/g,
    (digit) => digit + thousandsSeparator
  )

  if (normalizedPrecision <= 0) return wholePart

  return `${wholePart}${decimalSeparator}${decimalPart.padEnd(normalizedPrecision, '0')}`
}

/**
 * Formats an integer cents amount using a Shopify `shop.money_format`-style template string.
 *
 * @param {number} cents
 * @param {string} moneyFormat From `data-money-format` / {@link DEFAULT_MONEY_FORMAT}
 * @param {boolean} [stripTrailingZeros]
 * @returns {string}
 */
export function formatMoney(cents, moneyFormat, stripTrailingZeros = false) {
  const template = moneyFormat || DEFAULT_MONEY_FORMAT

  return template.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, placeholder) => {
    let thousandsSeparator = ','
    let decimalSeparator = '.'
    let precision = 2

    if (placeholder === 'amount_no_decimals') {
      precision = 0
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.'
      decimalSeparator = ','
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.'
      decimalSeparator = ','
      precision = 0
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' '
      precision = 0
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' '
      decimalSeparator = ','
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' '
      decimalSeparator = '.'
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'"
      decimalSeparator = '.'
    }

    return formatCents(
      cents,
      thousandsSeparator,
      decimalSeparator,
      precision,
      stripTrailingZeros
    )
  })
}

/**
 * @param {Element} parent
 * @param {string} selector
 * @param {string} className
 * @returns {HTMLElement}
 */
function ensureChild(parent, selector, className) {
  const existingElement = parent.querySelector(selector)
  if (existingElement instanceof HTMLElement) return existingElement

  const element = document.createElement('span')
  element.className = className
  parent.append(element)

  return element
}

/**
 * @param {ParentNode | null} parent
 * @param {string[]} selectors
 * @returns {HTMLElement | null}
 */
function queryFirstElement(parent, selectors) {
  if (!parent) return null

  for (const selector of selectors) {
    const match = parent.querySelector(selector)
    if (match instanceof HTMLElement) return match
  }

  return null
}

/**
 * PDP-006 — Builds one injected recap item for the supplied surface config.
 *
 * @param {BundleSummarySurfaceConfig} surfaceConfig
 * @param {string} value
 * @param {string} extraClass Marker class used to remove only our own rows on re-render.
 * @returns {HTMLElement}
 */
export function createRecapVariantItem(surfaceConfig, value, extraClass) {
  const item = document.createElement(surfaceConfig.itemTag)
  item.className = `${surfaceConfig.itemBaseClass} ${extraClass}`

  const variantValue = document.createElement('span')
  variantValue.className = surfaceConfig.valueClass
  variantValue.textContent = `+ ${value}`
  item.append(variantValue)

  return item
}

/**
 * PDP-006 — Builds one bundle variant item for the supplied surface config.
 *
 * @param {BundleSummarySurfaceConfig} surfaceConfig
 * @param {string} value
 * @returns {HTMLElement}
 */
export function createBundleVariantItem(surfaceConfig, value) {
  return createRecapVariantItem(
    surfaceConfig,
    value,
    surfaceConfig.itemBundleClass
  )
}

/**
 * PDP-006 — Resolves the descendant container that holds variant items for a marker.
 * The empty selector convention keeps `BUNDLE_SUMMARY_SURFACE_CONFIGS` literal and
 * means "marker is the container itself" (summary case).
 *
 * @param {HTMLElement} marker
 * @param {string} selector
 * @returns {HTMLElement | null}
 */
function resolveSurfaceChild(marker, selector) {
  if (!selector) return marker

  const match = marker.querySelector(selector)
  return match instanceof HTMLElement ? match : null
}

/**
 * PDP-006 — Single update path for every `[data-bundle-summary-source]` recap surface.
 * Removes previously-injected bundle and product-option items (matched by
 * `itemBundleClass` / `itemOptionClass`), appends one item per selected product option
 * followed by one per selected bundle entry, mirrors `hidden` on the configured target,
 * and syncs the visually-hidden screen-reader recap when present. Variant items rendered
 * by Liquid for real Shopify variant options are never touched.
 *
 * @param {HTMLElement} marker
 * @param {BundleSummarySurfaceConfig} surfaceConfig
 * @param {BundleState} bundleState
 * @param {OptionsState} [optionsState]
 * @returns {void}
 */
export function applyBundleItemsToSurface(
  marker,
  surfaceConfig,
  bundleState,
  optionsState = createEmptyOptionsState()
) {
  const container = resolveSurfaceChild(
    marker,
    surfaceConfig.itemContainerSelector
  )
  if (!(container instanceof HTMLElement)) return

  const injectedSelectors = [`.${surfaceConfig.itemBundleClass}`]
  if (surfaceConfig.itemOptionClass) {
    injectedSelectors.push(`.${surfaceConfig.itemOptionClass}`)
  }

  container
    .querySelectorAll(injectedSelectors.join(', '))
    .forEach((item) => item.remove())

  if (surfaceConfig.itemOptionClass) {
    for (const item of optionsState?.selectedItems ?? []) {
      container.append(
        createRecapVariantItem(
          surfaceConfig,
          item.label,
          surfaceConfig.itemOptionClass
        )
      )
    }
  }

  for (const item of bundleState.selectedItems) {
    container.append(createBundleVariantItem(surfaceConfig, item.productTitle))
  }

  const allItems = container.querySelectorAll(`.${surfaceConfig.itemBaseClass}`)
  const isEmpty = allItems.length === 0

  const hideTarget = resolveSurfaceChild(
    marker,
    surfaceConfig.hideTargetSelector
  )
  if (hideTarget instanceof HTMLElement) {
    hideTarget.hidden = isEmpty
  }

  if (surfaceConfig.accessibilityTextSelector) {
    const textTarget = resolveSurfaceChild(
      marker,
      surfaceConfig.accessibilityTextSelector
    )
    if (textTarget instanceof HTMLElement) {
      const summaryText = Array.from(allItems)
        .map((item) => {
          const valueEl = item.querySelector(`.${surfaceConfig.valueClass}`)
          if (!(valueEl instanceof HTMLElement)) return ''
          return valueEl.textContent.replace(/^\+\s*/, '').trim()
        })
        .filter(Boolean)
        .join(', ')

      textTarget.textContent = summaryText
    }
  }
}

/**
 * Selector for SSR-rendered bundle price surfaces that the theme paints with
 * `data-bundle-pending="true"`. The element shows a shimmer skeleton via CSS
 * until {@link BundleProductSync} reveals it (either after the first
 * `#applyBundleState`, after a `pgb:bundle-changed` event, or after the
 * safety timeout below). See `blocks/product-info-header.liquid`,
 * `blocks/product-info-summary.liquid`, `snippets/sticky-add-to-cart.liquid`.
 */
const BUNDLE_PENDING_SELECTOR = '[data-bundle-pending="true"]'

/**
 * Returns true when every currently-checked PGB checkbox carries both a
 * non-empty `data-product-title` AND a non-empty `data-variant-id`.
 *
 * PGB renders each row in two phases: it first marks the checkbox as checked
 * and writes the title, and only milliseconds later — after its AJAX
 * `/products/{handle}.js` lookup resolves — back-fills `data-variant-id` and
 * the real `data-price`. During that gap, `buildBundleStateFromPicker` /
 * `buildBundleStateFromEvent` see `selectedItems = [{ variantId: undefined,
 * finalPriceCents: 0 }]`. That state has `length > 0`, so the apply path
 * treats it as "user chose a free item" and writes `base + 0` on top of the
 * SSR floor — producing the visible `floor → base → floor` flicker captured
 * in runtime logs.
 *
 * Requiring `data-variant-id` is the cleanest signal that a row is fully
 * hydrated: a real PGB bundle line item always has a variant id (the cart
 * payload builder needs one). While the picker is unresolved we hold the
 * existing visible state — the SSR snapshot already reflects the required
 * floor — and let the next, settled event do the write.
 *
 * `data-price="0"` is intentionally allowed: zero-priced "included" items are
 * legitimate. They just need a real variant id to count as settled.
 *
 * @param {Element | null | undefined} picker
 * @returns {boolean}
 */
export function isPickerStateSettled(picker) {
  if (!(picker instanceof Element)) return true

  const checkedBoxes = picker.querySelectorAll('.pgb-checkbox:checked')

  for (const checkbox of checkedBoxes) {
    const title = (checkbox.getAttribute('data-product-title') || '').trim()
    if (!title) return false
    const variantId = (checkbox.getAttribute('data-variant-id') || '').trim()
    if (!variantId) return false
  }

  return true
}

/**
 * Builds a stable string key for the supplied bundle state so duplicate apply
 * passes (e.g. rAF bootstrap polling, MutationObserver firing for unrelated
 * attribute changes) can be skipped without stomping the DOM.
 *
 * @param {BundleState} bundleState
 * @returns {string}
 */
export function computeBundleStateKey(bundleState) {
  if (!bundleState || !Array.isArray(bundleState.selectedItems)) return 'empty'

  if (bundleState.selectedItems.length === 0) return 'empty'

  return bundleState.selectedItems
    .map((item) => {
      const variantId = (item?.variantId || '').toString()
      const productId = (item?.productId || '').toString()
      const finalCents = Number.isFinite(item?.finalPriceCents)
        ? item.finalPriceCents
        : 0
      const originalCents = Number.isFinite(item?.originalPriceCents)
        ? item.originalPriceCents
        : 0
      return `${variantId || productId}:${originalCents}:${finalCents}`
    })
    .join('|')
}

/**
 * Hard ceiling on how long the skeleton can stay visible without an explicit
 * reveal signal. Picked to cover the PGB picker's AJAX `/products/{handle}.js`
 * round-trip on slow connections without leaving a stuck skeleton if the
 * picker is missing entirely.
 */
const BUNDLE_PENDING_SAFETY_TIMEOUT_MS = 1500

/**
 * Reveal every SSR-flagged bundle price surface inside the section. Idempotent:
 * subsequent calls re-query and skip already-revealed elements.
 *
 * @param {HTMLElement | null | undefined} section
 */
export function revealBundlePendingPrices(section) {
  if (!(section instanceof HTMLElement)) return

  section.querySelectorAll(BUNDLE_PENDING_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLElement)) return
    element.setAttribute('data-bundle-pending', 'false')
  })
}

if (!customElements.get('bundle-product-sync')) {
  class BundleProductSync extends HTMLElement {
    /** @type {HTMLElement | null} */
    #section = null

    /** @type {Element | null} */
    #picker = null

    /** @type {Element | null} */
    #options = null

    /** @type {MutationObserver | null} */
    #pickerObserver = null

    /** @type {MutationObserver | null} */
    #sectionObserver = null

    /**
     * Dedicated observer for `<pgb-product-options>`. Kept separate from
     * `#sectionObserver`, which disconnects as soon as the bundle picker
     * appears and would otherwise never see a later options block.
     *
     * @type {MutationObserver | null}
     */
    #optionsSectionObserver = null

    /** @type {number | null} */
    #applyFrame = null

    /** @type {BundleState} */
    #latestBundleState = createEmptyBundleState()

    /** @type {OptionsState} */
    #latestOptionsState = createEmptyOptionsState()

    /** @type {boolean} */
    #latestOptionsValid = true

    /** @type {boolean} */
    #hasAppliedPriceOverlay = false

    /**
     * Stable key of the bundle state we last wrote to the DOM. Repeated apply
     * calls with the same key are no-ops, which prevents the rAF bootstrap
     * loop (and noisy MutationObserver passes) from re-running formatMoney
     * and stomping textContent on every frame.
     *
     * @type {string | null}
     */
    #lastAppliedStateKey = null

    /** @type {AbortController | null} */
    #pickerChangeAbort = null

    /** @type {number | null} */
    #bootstrapFrame = null

    /** @type {number} */
    #bootstrapAttempt = 0

    /** @type {string | null} */
    #bootstrapPreviousSnapshotKey = null

    /** @type {number | null} */
    #pendingRevealTimeout = null

    connectedCallback() {
      const section = this.closest('.shopify-section, dialog')
      if (!(section instanceof HTMLElement)) return

      this.#section = section
      this.#section.addEventListener(
        'pgb:bundle-changed',
        this.#handleBundleChange
      )
      this.#section.addEventListener(
        'pgb:options-changed',
        this.#handleOptionsChange
      )
      this.#section.addEventListener(
        StandardEvents.productSelect,
        this.#handleProductSelect
      )

      this.#bindPicker()
      this.#bindOptions()
      this.#armBundlePendingSafetyTimeout()
    }

    disconnectedCallback() {
      this.#section?.removeEventListener(
        'pgb:bundle-changed',
        this.#handleBundleChange
      )
      this.#section?.removeEventListener(
        'pgb:options-changed',
        this.#handleOptionsChange
      )
      this.#section?.removeEventListener(
        StandardEvents.productSelect,
        this.#handleProductSelect
      )

      this.#pickerChangeAbort?.abort()
      this.#pickerChangeAbort = null

      this.#pickerObserver?.disconnect()
      this.#sectionObserver?.disconnect()
      this.#optionsSectionObserver?.disconnect()

      if (this.#applyFrame !== null) {
        cancelAnimationFrame(this.#applyFrame)
      }

      if (this.#pendingRevealTimeout !== null) {
        clearTimeout(this.#pendingRevealTimeout)
        this.#pendingRevealTimeout = null
      }

      this.#lastAppliedStateKey = null

      this.#stopPickerBootstrap()
    }

    #armBundlePendingSafetyTimeout() {
      if (this.#pendingRevealTimeout !== null) return

      this.#pendingRevealTimeout = window.setTimeout(() => {
        this.#pendingRevealTimeout = null
        revealBundlePendingPrices(this.#section)
      }, BUNDLE_PENDING_SAFETY_TIMEOUT_MS)
    }

    /**
     * @param {Event} event
     */
    #handlePickerChange = (event) => {
      const target = event.target
      if (!(target instanceof HTMLInputElement)) return
      if (!target.classList.contains('pgb-checkbox')) return

      this.#reconcilePickerState()
    }

    /**
     * @param {object} [args]
     * @param {unknown} [args.detail] `pgb:bundle-changed` detail.
     * @param {unknown} [args.optionsDetail] `pgb:options-changed` detail.
     */
    #reconcilePickerState({ detail = null, optionsDetail = null } = {}) {
      if (!(this.#section instanceof HTMLElement)) return

      if (
        detail &&
        typeof detail === 'object' &&
        'selectedItems' in detail &&
        Array.isArray(detail.selectedItems)
      ) {
        this.#latestBundleState = buildBundleStateFromEvent(detail)
      } else if (this.#picker instanceof Element) {
        this.#latestBundleState = buildBundleStateFromPicker(this.#picker)
      }

      if (this.#picker instanceof Element) {
        this.#disablePickerInterceptor(this.#picker)
      }

      // Only overwrite the cached option validity when we actually have a
      // signal. A reconcile triggered by something else (late bind, picker
      // mutation, variant morph) must not optimistically flip an unmet
      // required group back to valid.
      const optionsApi =
        /** @type {(Element & { isSelectionValid?: () => boolean }) | null} */ (
          this.#options
        )

      if (optionsDetail) {
        this.#latestOptionsValid = resolveOptionsSelectionValid(
          optionsDetail,
          this.#options
        )
      } else if (typeof optionsApi?.isSelectionValid === 'function') {
        this.#latestOptionsValid = readOptionsSelectionValid(this.#options)
      } else if (!(this.#options instanceof Element)) {
        this.#latestOptionsValid = true
      }

      const bundleValid = resolveBundleSelectionValid(detail, this.#picker)
      syncBundleBuyButtons(
        this.#section,
        bundleValid && this.#latestOptionsValid,
        this.#picker,
        this.#options
      )
      this.#scheduleApply()
    }

    #stopPickerBootstrap() {
      if (this.#bootstrapFrame !== null) {
        cancelAnimationFrame(this.#bootstrapFrame)
        this.#bootstrapFrame = null
      }

      this.#bootstrapAttempt = 0
      this.#bootstrapPreviousSnapshotKey = null
    }

    #startPickerBootstrap() {
      this.#stopPickerBootstrap()
      if (!(this.#picker instanceof Element)) return

      this.#reconcilePickerState()

      if (!pickerHasRequiredGroups(this.#picker)) return
      if (resolveBundleSelectionValid(null, this.#picker)) return

      this.#schedulePickerBootstrapFrame()
    }

    #schedulePickerBootstrapFrame() {
      this.#bootstrapFrame = requestAnimationFrame(() => {
        this.#bootstrapFrame = null
        this.#runPickerBootstrapFrame()
      })
    }

    #runPickerBootstrapFrame() {
      if (!(this.#picker instanceof Element)) {
        this.#stopPickerBootstrap()
        return
      }

      const snapshot = readPickerSelectionSnapshot(this.#picker)
      this.#reconcilePickerState()

      if (snapshot.isValid) {
        this.#stopPickerBootstrap()
        return
      }

      if (
        this.#bootstrapPreviousSnapshotKey !== null &&
        snapshot.key === this.#bootstrapPreviousSnapshotKey
      ) {
        this.#stopPickerBootstrap()
        return
      }

      this.#bootstrapPreviousSnapshotKey = snapshot.key
      this.#bootstrapAttempt += 1

      if (
        !shouldContinuePickerBootstrap(
          this.#picker,
          snapshot,
          this.#bootstrapAttempt
        )
      ) {
        this.#reconcilePickerState()
        this.#stopPickerBootstrap()
        return
      }

      this.#schedulePickerBootstrapFrame()
    }

    /**
     * @param {Event} event
     */
    #handleBundleChange = (event) => {
      if (!(event instanceof CustomEvent)) return

      this.#reconcilePickerState({ detail: event.detail })
    }

    /**
     * @param {Event} event
     */
    #handleOptionsChange = (event) => {
      if (!(event instanceof CustomEvent)) return

      if (event.target instanceof Element && event.target !== this.#options) {
        this.#options = event.target
      }

      this.#lastAppliedStateKey = null
      this.#reconcilePickerState({ optionsDetail: event.detail })
    }

    #handleProductSelect = async (event) => {
      try {
        const detail = await event.promise
        this.#handleVariantUpdate(detail)
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('[bundle-product-sync] Event promise rejected:', error)
        }
      }
    }

    /**
     * @param {unknown} [payload] Resolved `ProductSelectEvent` payload (`{ detail, variant }`).
     */
    #handleVariantUpdate = (payload) => {
      // `variant:update` morphs `data-bundle-base-price` on the section's price
      // containers, so the same bundle items may now total to a different
      // price. Invalidate the last-applied key so the equality guard in
      // `#applyBundleState` does not skip the recompute.
      this.#lastAppliedStateKey = null
      this.#bindPicker()
      this.#bindOptions()
      this.#reconcilePickerState()
      this.#dispatchVariantBridge(payload)
    }

    /**
     * PGB's `<pgb-product-options>` listens on `document` for `variant:change`
     * to re-derive its variant scope and base price; this theme only dispatches
     * `ProductSelectEvent`, and the variant radios live in a separate form so
     * the app's own `change` listener never fires either. Bridge the two so
     * variant-scoped option groups and app-side pricing stay in sync.
     *
     * @param {unknown} payload
     */
    #dispatchVariantBridge(payload) {
      const resolved = /** @type {Record<string, any> | undefined} */ (payload)
      const resource = resolved?.detail?.resource ?? resolved?.variant

      requestAnimationFrame(() => {
        // `variant-picker` resolves the promise with `resource: null` whenever it
        // could not read the variant JSON (and for combined-listing swaps), so
        // fall back to the buy-button form, which the morph has updated by now.
        // `resource.price` is already cents, matching `data-base-price`.
        dispatchVariantChangeBridge(
          resource?.id ?? this.#readFormVariantId(),
          resource?.price
        )
      })
    }

    /**
     * @returns {string} Variant id currently held by the buy-button form.
     */
    #readFormVariantId() {
      const root =
        this.#section instanceof HTMLElement ? this.#section : document
      const input = root.querySelector(
        'form[data-product-form] [name="id"], product-form-component [name="id"]'
      )

      return input instanceof HTMLInputElement ? input.value : ''
    }

    #bindPicker() {
      if (!(this.#section instanceof HTMLElement)) return

      const picker = this.#section.querySelector('pgb-bundle-picker')
      if (picker) {
        this.#ensurePickerDivider(picker)
        this.#watchPicker(picker)
        this.#disablePickerInterceptor(picker)
        return
      }

      this.#sectionObserver?.disconnect()
      this.#sectionObserver = new MutationObserver(() => {
        if (!(this.#section instanceof HTMLElement)) return

        const nextPicker = this.#section.querySelector('pgb-bundle-picker')
        if (!nextPicker) return

        this.#sectionObserver?.disconnect()
        this.#ensurePickerDivider(nextPicker)
        this.#watchPicker(nextPicker)
        this.#disablePickerInterceptor(nextPicker)
      })

      this.#sectionObserver.observe(this.#section, {
        childList: true,
        subtree: true
      })
    }

    /**
     * Bind `<pgb-product-options>` when present, otherwise wait for the app
     * block to hydrate. `pgb:options-changed` bubbles to the section, so the
     * observer only exists to give us an element reference for live reads and
     * validation before the first event lands.
     */
    #bindOptions() {
      if (!(this.#section instanceof HTMLElement)) return

      const options = this.#section.querySelector('pgb-product-options')
      if (options) {
        this.#optionsSectionObserver?.disconnect()
        this.#optionsSectionObserver = null
        if (this.#options !== options) {
          this.#options = options
          this.#lastAppliedStateKey = null
          this.#scheduleApply()
        }
        this.#alignOptionsVariant()
        return
      }

      this.#options = null

      if (this.#optionsSectionObserver) return

      this.#optionsSectionObserver = new MutationObserver(() => {
        if (!(this.#section instanceof HTMLElement)) return

        const nextOptions = this.#section.querySelector('pgb-product-options')
        if (!nextOptions) return

        this.#optionsSectionObserver?.disconnect()
        this.#optionsSectionObserver = null
        this.#options = nextOptions
        this.#lastAppliedStateKey = null
        this.#alignOptionsVariant()
        this.#reconcilePickerState()
      })

      this.#optionsSectionObserver.observe(this.#section, {
        childList: true,
        subtree: true
      })
    }

    /**
     * A block that upgraded after a variant switch (or that a morph re-inserted
     * with server markup for the previously selected variant) carries a stale
     * variant scope. Correct it as soon as we see it rather than waiting for the
     * submit-time guard in `product-form.js`, so the rendered cards and prices
     * match the variant the shopper will actually add.
     */
    #alignOptionsVariant() {
      syncProductOptionsVariant(this.#options, this.#readFormVariantId())
    }

    /**
     * @param {Element} picker
     */
    #watchPicker(picker) {
      if (this.#picker === picker) return

      this.#pickerChangeAbort?.abort()
      this.#pickerChangeAbort = new AbortController()

      this.#picker = picker
      this.#pickerObserver?.disconnect()
      picker.addEventListener('change', this.#handlePickerChange, {
        capture: true,
        signal: this.#pickerChangeAbort.signal
      })

      this.#pickerObserver = new MutationObserver(() => {
        if (this.#picker) {
          this.#ensurePickerDivider(this.#picker)
        }
        this.#reconcilePickerState()
      })

      this.#pickerObserver.observe(picker, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'checked',
          'data-price',
          'data-discount',
          'data-product-title',
          'data-variant-title',
          'data-variant-id'
        ]
      })

      this.#startPickerBootstrap()
    }

    /**
     * Neutralizes PGB's window-capture submit interceptor so the theme can own
     * /cart/add.js submission. Idempotent: subsequent calls become no-ops once
     * `submitHandler` has been cleared. If the vendor renames its API in a
     * future release, the typeof check below makes this a soft fallback.
     *
     * @param {Element} picker
     */
    #disablePickerInterceptor(picker) {
      // `pgb-bundle-picker` is a custom element exposing a `submitHandler`
      // function (registered on `window` with capture=true) and a `boundForm`
      // ref. Both are cleared so the vendor cannot re-attach later.
      const pickerWithHandler =
        /** @type {Element & {submitHandler?: ((event: Event) => void) | null, boundForm?: HTMLFormElement | null}} */ (
          picker
        )

      const handler = pickerWithHandler.submitHandler
      if (typeof handler === 'function') {
        try {
          window.removeEventListener('submit', handler, true)
        } catch {
          // Ignore — listener may already be gone.
        }
        pickerWithHandler.submitHandler = null
      }

      if (pickerWithHandler.boundForm) {
        pickerWithHandler.boundForm = null
      }
    }

    /**
     * Public API — called by `product-form-component` after a successful add to
     * cart so the picker UI snaps back to its empty state (matches the original
     * PGB behavior of clearing the selection on success).
     */
    resetPickerSelection() {
      if (!(this.#picker instanceof Element)) {
        // Without a picker we still surface an empty bundle event so any cached
        // UI overlays reset to the base variant price.
        this.#section?.dispatchEvent(
          new CustomEvent('pgb:bundle-changed', {
            bubbles: true,
            detail: {
              selectedItems: [],
              totalPrice: 0,
              validation: { isValid: true, unmetGroups: [] }
            }
          })
        )
        return
      }

      const checkedBoxes = this.#picker.querySelectorAll(
        '.pgb-checkbox:checked'
      )
      checkedBoxes.forEach((box) => {
        if (box instanceof HTMLInputElement) {
          box.checked = false
          box.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })

      // Surface an explicit empty bundle event so overlays reset to the base
      // variant price and buy buttons re-evaluate the (now unmet) selection.
      const pickerValidation =
        /** @type {{ getSelectionValidation?: () => { isValid?: boolean, unmetGroups?: unknown[] } }} */ (
          this.#picker
        ).getSelectionValidation?.()
      const validation =
        pickerValidation && typeof pickerValidation.isValid === 'boolean'
          ? {
              isValid: pickerValidation.isValid,
              unmetGroups: pickerValidation.unmetGroups ?? []
            }
          : {
              isValid: resolveBundleSelectionValid(null, this.#picker),
              unmetGroups: []
            }

      const emptyDetail = { selectedItems: [], totalPrice: 0, validation }
      this.#section?.dispatchEvent(
        new CustomEvent('pgb:bundle-changed', {
          bubbles: true,
          detail: emptyDetail
        })
      )
      this.#reconcilePickerState({ detail: emptyDetail })

      // Re-default required groups (PGB auto-select) so shoppers can add again.
      this.#startPickerBootstrap()
    }

    /**
     * Inserts the divider right after <pgb-bundle-picker>, if available.
     * @param {Element} picker
     */
    #ensurePickerDivider(picker) {
      if (!(this.#section instanceof HTMLElement)) return
      if (!(picker instanceof Element)) return

      const existing = this.#section.querySelector(
        '[data-pgb-bundle-picker-divider]'
      )
      if (existing && existing.previousElementSibling === picker) return

      const template = this.#section.querySelector(
        'template[data-pgb-bundle-picker-divider-template]'
      )
      if (!(template instanceof HTMLTemplateElement)) return

      const next = picker.nextElementSibling
      if (next?.matches?.('[data-pgb-bundle-picker-divider]')) return

      const fragment = /** @type {DocumentFragment} */ (
        template.content.cloneNode(true)
      )
      const wrapper = fragment.querySelector('[data-pgb-bundle-picker-divider]')
      if (wrapper instanceof HTMLElement) {
        wrapper.removeAttribute('hidden')
      }

      const divider = fragment.querySelector('[data-pgb-bundle-picker-divider]')
      if (!divider) return

      picker.insertAdjacentElement('afterend', divider)

      if (existing && existing.isConnected) {
        existing.remove()
      }
    }

    #scheduleApply() {
      if (this.#applyFrame !== null) {
        cancelAnimationFrame(this.#applyFrame)
      }

      this.#applyFrame = requestAnimationFrame(() => {
        this.#applyFrame = null
        this.#applyBundleState()
      })
    }

    /**
     * @returns {BundleState}
     */
    #getBundleState() {
      if (
        this.#picker instanceof Element &&
        this.#picker.querySelector('.pgb-checkbox')
      ) {
        this.#latestBundleState = buildBundleStateFromPicker(this.#picker)
      }

      return this.#latestBundleState
    }

    /**
     * Live read of `<pgb-product-options>`. The app clears `innerHTML` when no
     * group applies to the current variant, so a live read can never return a
     * stale selection after the block hides.
     *
     * @returns {OptionsState}
     */
    #getOptionsState() {
      this.#latestOptionsState = buildOptionsStateFromElement(this.#options)

      return this.#latestOptionsState
    }

    /**
     * Applies the current bundle + option selection to every price / recap surface,
     * then nudges the Alma widget with the resulting displayed total. The refresh is
     * debounced inside `alma-widget-sync`, so calling it on every exit path (including
     * the early returns) is safe and guarantees Alma never lags a selection change.
     */
    #applyBundleState() {
      this.#applyBundleStateToSurfaces()
      scheduleAlmaWidgetRefresh(this.#section)
    }

    #applyBundleStateToSurfaces() {
      if (!(this.#section instanceof HTMLElement)) return

      // Picker is mid-hydration (checked boxes still missing variant id / price
      // / title attributes). Treating that as "user wants base price" produces
      // a visible flicker between SSR floor and JS overlay. Hold the current
      // visible state — the SSR snapshot already reflects the required floor.
      // Options are painted by the next settled pass, which is safe because
      // their SSR-equivalent state is "no surcharge".
      if (!isPickerStateSettled(this.#picker)) {
        revealBundlePendingPrices(this.#section)
        return
      }

      const bundleState = this.#getBundleState()
      const optionsState = this.#getOptionsState()
      const hasBundle = bundleState.selectedItems.length > 0
      const optionAdjustmentCents = optionsState.adjustmentCents
      const stateKey = `${computeBundleStateKey(bundleState)}#${computeOptionsStateKey(optionsState)}`

      // Bootstrap polling + MutationObserver passes can call us many times in
      // a row with identical state. Skip duplicate writes so we never stomp
      // textContent unnecessarily (and never accidentally reorder the recap).
      if (stateKey === this.#lastAppliedStateKey) {
        revealBundlePendingPrices(this.#section)
        return
      }

      // Recap always re-renders so a 0¢ included default still shows up.
      this.#cleanupBundleVariants(bundleState, optionsState)

      if (hasBundle || optionAdjustmentCents !== 0) {
        this.#updateHeaderPrice(bundleState, optionAdjustmentCents)
        this.#updateSummaryPrice(bundleState, optionAdjustmentCents)
        this.#updateStickyPrice(bundleState, optionAdjustmentCents)
        this.#hasAppliedPriceOverlay = true
        this.#lastAppliedStateKey = stateKey
        revealBundlePendingPrices(this.#section)
        return
      }

      if (!this.#hasAppliedPriceOverlay) {
        this.#lastAppliedStateKey = stateKey
        revealBundlePendingPrices(this.#section)
        return
      }

      const emptyBundleState = createEmptyBundleState()
      this.#updateHeaderPrice(emptyBundleState, 0)
      this.#updateSummaryPrice(emptyBundleState, 0)
      this.#updateStickyPrice(emptyBundleState, 0)
      this.#hasAppliedPriceOverlay = false
      this.#lastAppliedStateKey = stateKey
      revealBundlePendingPrices(this.#section)
    }

    /**
     * PDP-006 — Walks every `[data-bundle-summary-source]` marker in the section and
     * applies the unified surface helper so summary + sticky recap stay in lockstep.
     * Falls back silently when a marker type is unknown (forwards-compatible if a
     * new surface key is introduced before its config lands).
     *
     * @param {BundleState} bundleState
     * @param {OptionsState} [optionsState]
     */
    #cleanupBundleVariants(bundleState, optionsState) {
      if (!(this.#section instanceof HTMLElement)) return

      const markers = this.#section.querySelectorAll(
        '[data-bundle-summary-source]'
      )
      markers.forEach((marker) => {
        if (!(marker instanceof HTMLElement)) return

        const surfaceKey = marker.dataset.bundleSummarySource
        if (!surfaceKey) return

        const surfaceConfig = BUNDLE_SUMMARY_SURFACE_CONFIGS[surfaceKey]
        if (!surfaceConfig) return

        applyBundleItemsToSurface(
          marker,
          surfaceConfig,
          bundleState,
          optionsState ?? createEmptyOptionsState()
        )
      })
    }

    /**
     * @param {BundleState} bundleState
     * @param {number} [optionAdjustmentCents]
     */
    #updateHeaderPrice(bundleState, optionAdjustmentCents = 0) {
      if (!(this.#section instanceof HTMLElement)) return

      const priceContainer = this.#section.querySelector(
        '.product-info-header__price'
      )
      if (!(priceContainer instanceof HTMLElement)) return

      const currentPrice = priceContainer.querySelector(
        '.product-info-header__current-price'
      )
      const currentRow = priceContainer.querySelector(
        '.product-info-header__current'
      )
      if (
        !(currentPrice instanceof HTMLElement) ||
        !(currentRow instanceof HTMLElement)
      ) {
        return
      }

      const moneyFormat = this.dataset.moneyFormat || DEFAULT_MONEY_FORMAT
      const basePriceCents = parseInteger(
        priceContainer.dataset.bundleBasePrice
      )
      const baseComparePriceCents = parseInteger(
        priceContainer.dataset.bundleBaseComparePrice
      )
      const {
        totalPriceCents,
        totalComparePriceCents,
        totalDiscountCents,
        hasDiscount
      } = computeBundlePriceTotals(
        basePriceCents,
        baseComparePriceCents,
        bundleState,
        optionAdjustmentCents
      )

      currentPrice.textContent = formatMoney(totalPriceCents, moneyFormat)
      updateHtPriceElement(priceContainer, totalPriceCents, (cents) =>
        formatMoney(cents, moneyFormat)
      )
      // Contract with `alma-widget-sync`: keep the machine-readable displayed total
      // in sync with the money string we just painted.
      priceContainer.dataset.displayTotalCents = String(totalPriceCents)

      const currentPromo = priceContainer.querySelector(
        '.product-info-header__promo'
      )
      if (!hasDiscount) {
        currentPromo?.remove()
        return
      }

      const promo =
        currentPromo instanceof HTMLElement
          ? currentPromo
          : document.createElement('div')
      if (!(currentPromo instanceof HTMLElement)) {
        promo.className = 'product-info-header__promo'
        priceContainer.insertBefore(promo, currentRow)
      }

      const compareAt = ensureChild(
        promo,
        '.product-info-header__compare-at',
        'product-info-header__compare-at'
      )
      const discount = ensureChild(
        promo,
        '.product-info-header__discount',
        'product-info-header__discount'
      )

      compareAt.textContent = formatMoney(totalComparePriceCents, moneyFormat)
      discount.textContent = formatBundleSaveDisplay(
        this.dataset.bundleSaveTemplate,
        formatMoney(totalDiscountCents, moneyFormat, true)
      )
    }

    /**
     * Updates only the summary price row with bundle + option totals.
     * Variant-item cleanup is handled by #cleanupBundleVariants.
     * @param {BundleState} bundleState
     * @param {number} [optionAdjustmentCents]
     */
    #updateSummaryPrice(bundleState, optionAdjustmentCents = 0) {
      if (!(this.#section instanceof HTMLElement)) return

      const priceRow = queryFirstElement(this.#section, SUMMARY_PRICE_SELECTORS)
      if (!(priceRow instanceof HTMLElement)) return

      const currentPrice = priceRow.querySelector(
        '.product-info-summary__current-price'
      )
      if (!(currentPrice instanceof HTMLElement)) return

      const moneyFormat = this.dataset.moneyFormat || DEFAULT_MONEY_FORMAT
      const basePriceCents = parseInteger(priceRow.dataset.bundleBasePrice)
      const baseComparePriceCents = parseInteger(
        priceRow.dataset.bundleBaseComparePrice
      )
      const {
        totalPriceCents,
        totalComparePriceCents,
        totalDiscountCents,
        hasDiscount
      } = computeBundlePriceTotals(
        basePriceCents,
        baseComparePriceCents,
        bundleState,
        optionAdjustmentCents
      )

      currentPrice.textContent = formatMoney(totalPriceCents, moneyFormat)
      updateHtPriceElement(priceRow, totalPriceCents, (cents) =>
        formatMoney(cents, moneyFormat)
      )
      priceRow.dataset.displayTotalCents = String(totalPriceCents)

      const currentCompareAt = priceRow.querySelector(
        '.product-info-summary__compare-at-price'
      )
      const currentDiscount = priceRow.querySelector(
        '.product-info-summary__discount'
      )

      if (!hasDiscount) {
        currentCompareAt?.remove()
        currentDiscount?.remove()
        return
      }

      const compareAt = ensureChild(
        priceRow,
        '.product-info-summary__compare-at-price',
        'product-info-summary__compare-at-price'
      )
      const discount = ensureChild(
        priceRow,
        '.product-info-summary__discount',
        'product-info-summary__discount'
      )

      compareAt.textContent = formatMoney(totalComparePriceCents, moneyFormat)
      discount.textContent = formatBundleSaveDisplay(
        this.dataset.bundleSaveTemplate,
        formatMoney(totalDiscountCents, moneyFormat, true)
      )
    }

    /**
     * @param {BundleState} bundleState
     * @param {number} [optionAdjustmentCents]
     */
    #updateStickyPrice(bundleState, optionAdjustmentCents = 0) {
      if (!(this.#section instanceof HTMLElement)) return

      const priceGroup = this.#section.querySelector('.sticky-atc__price-group')
      if (!(priceGroup instanceof HTMLElement)) return

      const currentPrice = priceGroup.querySelector(
        '.sticky-atc__price-current'
      )
      if (!(currentPrice instanceof HTMLElement)) return

      const moneyFormat = this.dataset.moneyFormat || DEFAULT_MONEY_FORMAT
      const basePriceCents = parseInteger(priceGroup.dataset.bundleBasePrice)
      const baseComparePriceCents = parseInteger(
        priceGroup.dataset.bundleBaseComparePrice
      )
      const {
        totalPriceCents,
        totalComparePriceCents,
        totalDiscountCents,
        hasDiscount
      } = computeBundlePriceTotals(
        basePriceCents,
        baseComparePriceCents,
        bundleState,
        optionAdjustmentCents
      )

      currentPrice.textContent = formatMoney(totalPriceCents, moneyFormat)
      updateHtPriceElement(priceGroup, totalPriceCents, (cents) =>
        formatMoney(cents, moneyFormat)
      )

      const currentCompareAt = priceGroup.querySelector(
        '.sticky-atc__price-compare'
      )
      const currentDiscount = priceGroup.querySelector(
        '.sticky-atc__price-discount'
      )

      if (!hasDiscount) {
        currentCompareAt?.remove()
        currentDiscount?.remove()
        return
      }

      const compareAt = ensureChild(
        priceGroup,
        '.sticky-atc__price-compare',
        'sticky-atc__price-compare'
      )
      const discount = ensureChild(
        priceGroup,
        '.sticky-atc__price-discount',
        'sticky-atc__price-discount'
      )

      compareAt.textContent = formatMoney(totalComparePriceCents, moneyFormat)
      discount.textContent = formatBundleSaveDisplay(
        this.dataset.bundleSaveTemplate,
        formatMoney(totalDiscountCents, moneyFormat)
      )
    }
  }

  customElements.define('bundle-product-sync', BundleProductSync)
}
